import React, { useCallback, useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import images from "../utils/imageLoader";

/**
 * Lightbox modal accesible y sin dependencias externas.
 * Recibe la lista de imágenes del proyecto abierto y el índice inicial.
 */
const Lightbox = ({ items, startIndex, onClose }) => {
  const [index, setIndex] = useState(startIndex);

  const goPrev = useCallback(
    () => setIndex((i) => (i - 1 + items.length) % items.length),
    [items.length]
  );
  const goNext = useCallback(
    () => setIndex((i) => (i + 1) % items.length),
    [items.length]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [goPrev, goNext, onClose]);

  const current = items[index];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={current.alt}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Cerrar galería"
        className="absolute top-4 right-4 text-white/90 hover:text-white p-2"
      >
        <FaTimes size={26} />
      </button>

      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          aria-label="Imagen anterior"
          className="absolute left-2 md:left-6 text-white/90 hover:text-white p-2"
        >
          <FaChevronLeft size={28} />
        </button>
      )}

      <figure
        className="max-w-4xl w-full max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[current.src]}
          alt={current.alt}
          className="max-h-[75vh] w-auto max-w-full object-contain rounded"
        />
        <figcaption className="mt-3 text-sm text-center text-white/90">
          {current.alt} ({index + 1}/{items.length})
        </figcaption>
      </figure>

      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          aria-label="Imagen siguiente"
          className="absolute right-2 md:right-6 text-white/90 hover:text-white p-2"
        >
          <FaChevronRight size={28} />
        </button>
      )}
    </div>
  );
};

/**
 * Mini-grid de evidencias con lightbox integrado. `items` es un arreglo de
 * `{ src, alt }` con rutas relativas a `src/assets/Images` (ver utils/imageLoader).
 * `maxThumbnails` limita cuántas miniaturas se muestran para no convertir la
 * tarjeta en una galería interminable; el resto solo se ve dentro del lightbox.
 */
const EvidenceGallery = ({ items, maxThumbnails = 6 }) => {
  const [openIndex, setOpenIndex] = useState(null);

  if (!items || items.length === 0) return null;

  const visibleThumbnails = items.slice(0, maxThumbnails);
  const remaining = items.length - visibleThumbnails.length;

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {visibleThumbnails.map((item, i) => {
          const isLastVisible = i === visibleThumbnails.length - 1;
          return (
            <button
              type="button"
              key={item.src}
              onClick={() => setOpenIndex(i)}
              className="relative aspect-square overflow-hidden rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label={`Ver imagen: ${item.alt}`}
            >
              <img
                src={images[item.src]}
                alt={item.alt}
                loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
              />
              {isLastVisible && remaining > 0 && (
                <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-semibold text-sm">
                  +{remaining}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {openIndex !== null && (
        <Lightbox
          items={items}
          startIndex={openIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </>
  );
};

export default EvidenceGallery;
